import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Extensions from './components/Extensions';
import ExtensionSettingsComponent from './components/ExtensionSettings';
import TimedMessages from './components/TimedMessages';
import Moderation from './components/Moderation';
import CustomCommands from './components/CustomCommands';
import Settings from './components/Settings';
import MagicLinkLogin from './components/MagicLinkLogin';
import SettingsSaveModal from './components/SettingsSaveModal';
import TtsOverlay from './components/TtsOverlay';
import { Extension, ExtensionSettings } from './types';
import { AuthProvider, useAuth } from './contexts/AuthContext';

function AppContent() {
  const { user, signOut } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Check if we're on the TTS overlay route
  const isTtsOverlay = window.location.pathname === '/tts-overlay';

  const [activeSection, setActiveSection] = useState('extensions');
  const [activeExtensionSettings, setActiveExtensionSettings] = useState<string | null>(null);
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [extensionSettings, setExtensionSettings] = useState<ExtensionSettings>({});
  const [showSettingsSaveModal, setShowSettingsSaveModal] = useState(false);
  const [recentlyAddedExtension, setRecentlyAddedExtension] = useState<string | null>(null);

  const activatedExtensions = extensions.filter(ext => ext.activated);

  // Redirect away from settings if user logs out
  useEffect(() => {
    if (!user && activeSection === 'settings') {
      setActiveSection('extensions');
    }
  }, [user, activeSection]);

  const handleExtensionsLoaded = (loadedExtensions: Extension[], loadedExtensionSettings: ExtensionSettings) => {
    const previouslyActivated = new Set(extensions.filter(ext => ext.activated).map(ext => ext.id));
    const newlyActivated = loadedExtensions.filter(ext => ext.activated && !previouslyActivated.has(ext.id));
    
    setExtensions(loadedExtensions);
    setExtensionSettings(loadedExtensionSettings);
    
    // Show animation for newly activated extensions
    if (newlyActivated.length > 0) {
      setRecentlyAddedExtension(newlyActivated[0].id);
      setTimeout(() => setRecentlyAddedExtension(null), 2500);
    }
  };

  const handleExtensionClick = (extensionId: string) => {
    setActiveExtensionSettings(extensionId);
    setActiveSection('extension-settings');
  };

  // COMMENTED OUT - HANDLERS FOR FULL FUNCTIONALITY PRESERVED FOR FUTURE USE
  /*
  const handleAddTimedMessage = (message: Omit<TimedMessage, 'id'>) => {
    const newMessage: TimedMessage = {
      ...message,
      id: `msg${Date.now()}`
    };
    setTimedMessages([...timedMessages, newMessage]);
  };

  const handleUpdateTimedMessage = (updatedMessage: TimedMessage) => {
    setTimedMessages(messages =>
      messages.map(msg => msg.id === updatedMessage.id ? updatedMessage : msg)
    );
  };

  const handleDeleteTimedMessage = (id: string) => {
    setTimedMessages(messages => messages.filter(msg => msg.id !== id));
  };

  const handleToggleTimedMessage = (id: string, enabled: boolean) => {
    setTimedMessages(messages =>
      messages.map(msg => msg.id === id ? { ...msg, enabled } : msg)
    );
  };

  const handleAddCustomCommand = (command: Omit<CustomCommand, 'id'>) => {
    const newCommand: CustomCommand = {
      ...command,
      id: `cmd${Date.now()}`
    };
    setCustomCommands([...customCommands, newCommand]);
  };

  const handleUpdateCustomCommand = (updatedCommand: CustomCommand) => {
    setCustomCommands(commands =>
      commands.map(cmd => cmd.id === updatedCommand.id ? updatedCommand : cmd)
    );
  };

  const handleDeleteCustomCommand = (id: string) => {
    setCustomCommands(commands => commands.filter(cmd => cmd.id !== id));
  };
  */

  const renderContent = () => {
    switch (activeSection) {
      case 'extensions':
        return (
          <Extensions
            onExtensionsLoaded={handleExtensionsLoaded}
            onNavigateToSettings={() => setActiveSection('settings')}
          />
        );
      case 'extension-settings':
        return activeExtensionSettings ? (
          <ExtensionSettingsComponent
            extensionId={activeExtensionSettings}
            settings={extensionSettings}
          />
        ) : null;
      case 'timed-messages':
        return <TimedMessages onLogin={() => setShowLoginModal(true)} onNavigateToSettings={() => setActiveSection('settings')} />;
      case 'moderation':
        return <Moderation />;
      case 'custom-commands':
        return <CustomCommands onLogin={() => setShowLoginModal(true)} onNavigateToSettings={() => setActiveSection('settings')} />;
      case 'settings':
        // Only allow settings access for authenticated users
        return user ? (
          <Settings isConnected={!!user} onSaveSettings={() => setShowSettingsSaveModal(true)} />
        ) : (
          <div className="content-section flex items-center justify-center min-h-[500px]">
            <div className="max-w-md w-full text-center p-8 border-2 border-white bg-black">
              <div className="mb-6">
                <i className="fa-solid fa-lock text-6xl text-white mb-4 inline-block"></i>
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Authentication Required</h2>
              <p className="text-gray-300 mb-6 text-base leading-relaxed">
                Sign in to access your settings and manage your messages. We use magic link authentication - no passwords needed!
              </p>

              <div className="space-y-4">
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="paint-button w-full text-lg py-3"
                >
                  <i className="fa-solid fa-envelope mr-2"></i>
                  Sign In with Magic Link
                </button>

                <div className="flex items-center gap-2 text-xs text-gray-400 justify-center">
                  <i className="fa-solid fa-info-circle"></i>
                  <span>We'll send a secure link to your email</span>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // If we're on the TTS overlay route, render only the overlay component
  if (isTtsOverlay) {
    return <TtsOverlay />;
  }

  return (
    <div className="flex h-screen bg-black text-sm z-10 relative">
      <Sidebar
        user={user}
        onLogin={() => setShowLoginModal(true)}
        onLogout={signOut}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        activatedExtensions={activatedExtensions}
        onExtensionClick={handleExtensionClick}
        activeExtensionSettings={activeExtensionSettings}
        recentlyAddedExtension={recentlyAddedExtension}
      />

      <main className="flex-1 p-6 overflow-y-auto">
        <div id="content-area">
          {renderContent()}
        </div>
      </main>

      {showLoginModal && (
        <MagicLinkLogin onClose={() => setShowLoginModal(false)} />
      )}


      <SettingsSaveModal
        isOpen={showSettingsSaveModal}
        onClose={() => setShowSettingsSaveModal(false)}
        isLoggedIn={!!user}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;